import {isValidPhoneNumber} from 'libphonenumber-js';
import isEmail from 'validator/lib/isEmail';

import {createHubspotMeta} from '../../../../utils/bookingUtils';
import {portalGetMatch} from '../../../../utils/onboardingUtils';
import logTrackingEvent, {
  FAAFDataLayerValues,
  TRACKING_DEFAULTS,
} from '../../../commons/FormsLibrary/utils/logTrackingEvent';
import {handleNextFactory, onSubmitFactory, validatorsFactory} from '../form';
import {OnboardingPayload} from '../types';

export const validators = validatorsFactory({
  hasAdvisor: (value) => typeof value !== 'undefined',
  goals: (values) => values?.size > 0,
  household_income: (value) => typeof value !== 'undefined',
  assets: (values) => values?.size > 0,
  age: (value) => value >= 18,
  name: (value) => !!value?.length,
  last_name: (value) => !!value?.length,
  email: (value) => !!value && isEmail(value),
  mobile: (value) => !!value && isValidPhoneNumber(value, 'US'),
});

export const onSubmit = onSubmitFactory(async (values, {additionalProps}) => {
  const body: OnboardingPayload = {
    has_advisor: values.hasAdvisor,
    goals: [],
    age: String(values.age),
    salary: String(values.household_income),
    assets: {},
    location: values.location,
    name: values.name,
    last_name: values.last_name,
    email: values.email,
    // Remove mask (xxx) xx-xxx-xx
    mobile: values.mobile.replace(/[()_-]| /g, ''),
    // Required for core-Hubspot user tracking
    meta: createHubspotMeta(),
    source: additionalProps.source,
  };

  // Mapped from Core DB data
  const totalByAssetId: Record<number, number> = {
    // 5: Business (unused)
    1: values.assets_cash,
    2: values.assets_real_state,
    3: values.assets_investments,
    4: values.assets_retirement,
    6: values.assets_other,
  };

  values.goals.forEach((goal) => body.goals.push(goal));
  values.assets.forEach((assetID, index) => {
    const total = totalByAssetId[assetID];

    if (typeof total !== 'undefined') {
      body.assets[index] = {
        asset_type_id: String(assetID),
        total: String(total),
      };
    }
  });

  return new Promise((reject) => {
    portalGetMatch({body}, (error) => {
      if (error) {
        reject(error);

        return;
      }
    }).catch(() => undefined);
  });
});

export const handleNext = handleNextFactory(
  ({values, navigation, additionalProps}, newEntry) => {
    // Skip/show assets
    const canContinueByStep: {
      [Step in typeof newEntry.step]?: boolean;
    } = {
      assetsCash: !!values.assets?.has(1),
      assetsRealState: !!values.assets?.has(2),
      assetsInvestments: !!values.assets?.has(3),
      assetsRetirement: !!values.assets?.has(4),
      assetsOther: !!values.assets?.has(6),
    };

    const canContinue =
      typeof canContinueByStep[newEntry.step] !== 'undefined'
        ? canContinueByStep[newEntry.step]
        : true;

    if (canContinue && navigation.history.length > 0) {
      const getActionByStep = (): FAAFDataLayerValues['action'] => {
        const lastEntry = navigation.history[navigation.history.length - 1];

        if (lastEntry.step === 'hasAdvisor') return 'experience';
        if (lastEntry.step === 'goals') return 'goals';
        if (lastEntry.step === 'age') return 'age';
        if (lastEntry.step === 'income') return 'income';
        if (
          lastEntry.step === 'assets' ||
          lastEntry.step === 'assetsCash' ||
          lastEntry.step === 'assetsRealState' ||
          lastEntry.step === 'assetsInvestments' ||
          lastEntry.step === 'assetsRetirement' ||
          lastEntry.step === 'assetsOther'
        )
          return 'assets';
        if (lastEntry.step === 'location') return 'location';
        if (lastEntry.step === 'about') return 'about you';

        return null;
      };

      const trackingValues: FAAFDataLayerValues = {
        ...TRACKING_DEFAULTS,
        action: getActionByStep(),
      };
      const stepsInHistory = navigation.history.filter(({skipped}) => !skipped);

      const getUserAssetValue = () => {
        let total = 0;

        // Add only investable assets (cash, investments, retirement)
        stepsInHistory.forEach(({step}) => {
          if (step === 'assetsCash') {
            total += values.assets_cash;
          } else if (step === 'assetsInvestments') {
            total += values.assets_investments;
          } else if (step === 'assetsRetirement') {
            total += values.assets_retirement;
          }
        });

        return total;
      };

      const addValueByField: Record<typeof newEntry.step, () => void> = {
        intro: null,
        animation: null,
        hasAdvisor: () => {
          trackingValues.user_experience = values.hasAdvisor ? 'Yes' : 'No';
        },
        goals: () => {
          let index = 0;

          values.goals.forEach((goal) => {
            trackingValues[`user_goal_${index + 1}`] = goal;
            index += 1;
          });
        },
        age: () => {
          trackingValues.user_age = values.age;
        },
        income: () => {
          trackingValues.user_salary = values.household_income;
        },
        assets: () => {
          values.assets.forEach((asset) => {
            const coreAsset = additionalProps.coreAssets.find(
              ({id}) => id === asset,
            );

            if (coreAsset) {
              trackingValues[`assets_${coreAsset.id}`] = coreAsset.name;
            }
          });
        },
        assetsCash: () => {
          trackingValues.user_asset_value = getUserAssetValue();
        },
        assetsRealState: () => {
          trackingValues.user_asset_value = getUserAssetValue();
        },
        assetsInvestments: () => {
          trackingValues.user_asset_value = getUserAssetValue();
        },
        assetsRetirement: () => {
          trackingValues.user_asset_value = getUserAssetValue();
        },
        assetsOther: () => {
          trackingValues.user_asset_value = getUserAssetValue();
        },
        location: () => {
          trackingValues.user_location = values.location;
        },
        about: () => {
          // NOTE: Do NOT log personal info in dataLayer (email/phone)

          let fullName = values.name;

          if (values.last_name) {
            fullName += ` ${values.last_name}`;
          }

          trackingValues.user_name = fullName;
        },
      };

      stepsInHistory.forEach(({step}) => addValueByField[step]?.());

      logTrackingEvent({
        values: trackingValues,
        currentStep: navigation.history[navigation.history.length - 1].step,
      });
    }

    return canContinue;
  },
);
